/**
 * Encrypted File Viewer Component
 *
 * Allows users to decrypt and view encrypted evidence files.
 * Supports preview, download, save, and print functionality.
 */

import React, { useState, useRef, useEffect } from 'react';
import encryptionService from '../services/encryptionService';
import './EncryptedFileViewer.css';

function EncryptedFileViewer({ verificationCode, blobUrl, encryptedData, record }) {
  const [decryptionKey, setDecryptionKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [decrypting, setDecrypting] = useState(false);
  const [decryptedFile, setDecryptedFile] = useState(null);
  const [error, setError] = useState('');
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Zoom and crop states
  const [zoom, setZoom] = useState(1);
  const [cropMode, setCropMode] = useState(false);
  const [cropStart, setCropStart] = useState(null);
  const [cropEnd, setCropEnd] = useState(null);
  const [showCropPreview, setShowCropPreview] = useState(false);

  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Reset zoom and crop states when decrypted file changes
    if (decryptedFile) {
      setZoom(1);
      setCropMode(false);
      setCropStart(null);
      setCropEnd(null);
      setShowCropPreview(false);
    }
  }, [decryptedFile]);

  const handleDecrypt = async () => {
    setError('');
    setDecrypting(true);

    try {
      // Validate key first
      const validation = encryptionService.validateKey(decryptionKey);
      if (!validation.isValid) {
        setError(validation.error);
        setDecrypting(false);
        return;
      }

      // Fetch encrypted data if we have a blob URL
      let encryptedContent = encryptedData;

      if (!encryptedContent && blobUrl) {
        console.log('[EncryptedFileViewer] Fetching encrypted file from:', blobUrl);
        const response = await fetch(blobUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch encrypted file');
        }
        // Raw bytes: handles both the EVC2 container and legacy base64 text
        encryptedContent = await response.arrayBuffer();
      }

      if (!encryptedContent) {
        setError('No encrypted data available');
        setDecrypting(false);
        return;
      }

      // Decrypt the file
      console.log('[EncryptedFileViewer] Decrypting file...');
      const result = await encryptionService.decryptFile(
        encryptedContent,
        decryptionKey,
        record?.mimeType || 'application/octet-stream'
      );

      if (!result.success) {
        setError(result.error || 'Decryption failed. Please check your key.');
        setDecrypting(false);
        return;
      }

      setDecryptedFile({
        blob: result.blob,
        url: result.url,
        type: result.type,
        size: result.size,
        originalName: record?.fileName || 'decrypted-file'
      });

      console.log('[EncryptedFileViewer] Decryption successful');

    } catch (err) {
      console.error('[EncryptedFileViewer] Decryption error:', err);
      setError('Failed to decrypt file. Please verify your encryption key.');
    } finally {
      setDecrypting(false);
    }
  };

  const handleDownload = () => {
    if (!decryptedFile) return;

    const extension = encryptionService.getFileExtension(
      decryptedFile.originalName,
      decryptedFile.type
    );
    const filename = encryptionService.generateFilename('decrypted', extension);

    encryptionService.downloadBlob(decryptedFile.blob, filename);
  };

  const handlePrint = () => {
    if (!decryptedFile) return;
    encryptionService.printBlob(decryptedFile.blob);
  };

  // Zoom handlers
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setZoom(1);
  };

  // Crop handlers
  const toggleCropMode = () => {
    if (cropMode) {
      // Exiting crop mode
      setCropStart(null);
      setCropEnd(null);
      setShowCropPreview(false);
    }
    setCropMode(!cropMode);
  };

  const handleMouseDown = (e) => {
    if (!cropMode || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCropStart({ x, y });
    setCropEnd({ x, y });
    setShowCropPreview(false);
  };

  const handleMouseMove = (e) => {
    if (!cropMode || !cropStart || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCropEnd({ x, y });
  };

  const handleMouseUp = () => {
    if (!cropMode || !cropStart || !cropEnd) return;
    // Crop selection complete
    setShowCropPreview(true);
  };

  const cancelCrop = () => {
    setCropStart(null);
    setCropEnd(null);
    setShowCropPreview(false);
  };

  const getCropStyles = () => {
    if (!cropStart || !cropEnd) return {};

    const left = Math.min(cropStart.x, cropEnd.x);
    const top = Math.min(cropStart.y, cropEnd.y);
    const width = Math.abs(cropEnd.x - cropStart.x);
    const height = Math.abs(cropEnd.y - cropStart.y);

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
  };

  const canPreview = decryptedFile && encryptionService.canPreview(decryptedFile.type);
  const isImage = decryptedFile && decryptedFile.type.startsWith('image/');

  return (
    <div className="encrypted-viewer-container">
      <div className="encrypted-viewer-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>🔒 Encrypted Evidence File</h3>
          <button
            className="btn btn-info-small"
            onClick={() => setShowInfoModal(true)}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ℹ️ How to Decrypt
          </button>
        </div>
        <div style={{
          padding: '1rem',
          background: '#fef3c7',
          border: '2px solid #fbbf24',
          borderRadius: '6px',
          marginTop: '1rem'
        }}>
          <p style={{ margin: 0, color: '#92400e', fontSize: '0.95rem', lineHeight: 1.6 }}>
            <strong>This evidence file contains encrypted data.</strong> It cannot be displayed directly. To view the original file content, you must enter the encryption key that was used during upload.
          </p>
        </div>
      </div>

      {!decryptedFile && (
        <div className="decryption-form">
          <div className="form-group">
            <label htmlFor="decryptionKey">Decryption Key</label>
            <div className="key-input-wrapper">
              <input
                type={showKey ? 'text' : 'password'}
                id="decryptionKey"
                value={decryptionKey}
                onChange={(e) => setDecryptionKey(e.target.value)}
                placeholder="Enter your encryption key"
                disabled={decrypting}
                onKeyPress={(e) => e.key === 'Enter' && handleDecrypt()}
              />
              <button
                type="button"
                className="toggle-key-btn"
                onClick={() => setShowKey(!showKey)}
                disabled={decrypting}
                title={showKey ? 'Hide key' : 'Show key'}
              >
                👁️
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              <span className="error-icon">⚠</span>
              {error}
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={handleDecrypt}
            disabled={!decryptionKey || decrypting}
          >
            {decrypting ? (
              <>
                <span className="spinner-small"></span>
                Decrypting...
              </>
            ) : (
              <>
                <span>🔓</span>
                Decrypt and View
              </>
            )}
          </button>
        </div>
      )}

      {decryptedFile && (
        <div className="decrypted-content">
          <div className="alert alert-success">
            <h4>✓ Decryption Successful</h4>
            <p>File decrypted successfully. Original file: <strong>{decryptedFile.originalName}</strong></p>
          </div>

          {/* Toolbar for viewing controls (only show for images) */}
          {isImage && (
            <div className="evidence-viewer-toolbar" style={{
              display: 'flex',
              gap: '0.5rem',
              padding: '1rem',
              background: '#f9fafb',
              borderRadius: '8px',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              alignItems: 'center',
              border: '1px solid #e5e7eb'
            }}>
              <button
                className="btn btn-icon"
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                title="Zoom out"
                style={{
                  padding: '0.5rem 1rem',
                  background: zoom <= 0.5 ? '#e5e7eb' : 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: zoom <= 0.5 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span className="icon">🔍−</span>
              </button>

              <span className="zoom-level" style={{
                padding: '0.5rem 1rem',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontWeight: '500',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                {Math.round(zoom * 100)}%
              </span>

              <button
                className="btn btn-icon"
                onClick={handleZoomIn}
                disabled={zoom >= 3}
                title="Zoom in"
                style={{
                  padding: '0.5rem 1rem',
                  background: zoom >= 3 ? '#e5e7eb' : 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: zoom >= 3 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span className="icon">🔍+</span>
              </button>

              <button
                className="btn btn-icon"
                onClick={handleResetZoom}
                title="Reset zoom"
                style={{
                  padding: '0.5rem 1rem',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span className="icon">⟲</span> Reset
              </button>

              <div style={{
                width: '1px',
                height: '24px',
                background: '#d1d5db',
                margin: '0 0.5rem'
              }}></div>

              <button
                className={`btn btn-icon ${cropMode ? 'active' : ''}`}
                onClick={toggleCropMode}
                title="Crop and analyze region"
                style={{
                  padding: '0.5rem 1rem',
                  background: cropMode ? '#3b82f6' : 'white',
                  color: cropMode ? 'white' : 'inherit',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span className="icon">✂️</span> {cropMode ? 'Exit Crop' : 'Crop'}
              </button>
            </div>
          )}

          {/* Crop instructions */}
          {isImage && cropMode && (
            <div style={{
              padding: '0.75rem 1rem',
              background: '#eff6ff',
              border: '1px solid #3b82f6',
              borderRadius: '6px',
              marginBottom: '1rem',
              color: '#1e40af',
              fontSize: '0.9rem'
            }}>
              <p style={{ margin: 0 }}>
                🔧 <strong>Crop Mode Active:</strong> Click and drag on the image to select a region for detailed analysis
              </p>
            </div>
          )}

          {/* Preview Section */}
          {canPreview && (
            <div className="preview-section">
              <h4>Preview</h4>
              <div
                className="preview-container"
                ref={containerRef}
                style={{
                  cursor: isImage && cropMode ? 'crosshair' : 'default',
                  overflow: isImage ? 'auto' : 'visible',
                  maxHeight: '600px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1rem',
                  background: '#f9fafb',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {decryptedFile.type.startsWith('image/') && (
                  <div
                    style={{
                      position: 'relative',
                      display: 'inline-block'
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                  >
                    <img
                      ref={imageRef}
                      src={decryptedFile.url}
                      alt="Decrypted evidence"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '600px',
                        objectFit: 'contain',
                        transform: `scale(${zoom})`,
                        transformOrigin: 'center center',
                        transition: 'transform 0.2s ease',
                        userSelect: 'none'
                      }}
                      draggable={false}
                    />
                    {cropMode && cropStart && cropEnd && (
                      <div
                        style={{
                          position: 'absolute',
                          border: '2px dashed #3b82f6',
                          background: 'rgba(59, 130, 246, 0.1)',
                          pointerEvents: 'none',
                          ...getCropStyles()
                        }}
                      />
                    )}
                  </div>
                )}
                {decryptedFile.type === 'application/pdf' && (
                  <iframe
                    src={decryptedFile.url}
                    title="Decrypted PDF"
                    style={{ width: '100%', height: '600px', border: '1px solid #e5e7eb' }}
                  />
                )}
                {decryptedFile.type.startsWith('video/') && (
                  <video
                    controls
                    src={decryptedFile.url}
                    style={{ maxWidth: '100%', maxHeight: '600px' }}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
                {decryptedFile.type.startsWith('audio/') && (
                  <audio
                    controls
                    src={decryptedFile.url}
                    style={{ width: '100%' }}
                  >
                    Your browser does not support the audio tag.
                  </audio>
                )}
                {decryptedFile.type.startsWith('text/') && (
                  <iframe
                    src={decryptedFile.url}
                    title="Decrypted text"
                    style={{ width: '100%', height: '400px', border: '1px solid #e5e7eb' }}
                  />
                )}
              </div>
            </div>
          )}

          {!canPreview && (
            <div className="alert alert-info">
              <p>Preview not available for this file type. Use the download button to save the file.</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="btn btn-primary" onClick={handleDownload}>
              <span>💾</span>
              Download Decrypted File
            </button>

            {canPreview && (decryptedFile.type.startsWith('image/') || decryptedFile.type === 'application/pdf') && (
              <button className="btn btn-secondary" onClick={handlePrint}>
                <span>🖨</span>
                Print
              </button>
            )}

            <button
              className="btn btn-outline"
              onClick={() => {
                setDecryptedFile(null);
                setDecryptionKey('');
                setError('');
              }}
            >
              <span>🔒</span>
              Lock File
            </button>
          </div>

          <div className="file-info">
            <p><strong>File Type:</strong> {decryptedFile.type}</p>
            <p><strong>File Size:</strong> {(decryptedFile.size / 1024).toFixed(2)} KB</p>
          </div>
        </div>
      )}

      {/* Crop Preview Modal */}
      {showCropPreview && cropStart && cropEnd && imageRef.current && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}
          onClick={cancelCrop}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', color: '#1f2937' }}>
              Cropped Region Preview
            </h4>
            <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
              📸 Use your device's screenshot tool to capture this cropped region
            </p>

            <div style={{
              position: 'relative',
              overflow: 'hidden',
              maxWidth: '100%',
              maxHeight: '60vh',
              border: '2px solid #3b82f6',
              borderRadius: '8px',
              background: '#f9fafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={decryptedFile.url}
                alt="Cropped region"
                style={{
                  display: 'block',
                  position: 'relative',
                  marginLeft: `-${Math.min(cropStart.x, cropEnd.x)}px`,
                  marginTop: `-${Math.min(cropStart.y, cropEnd.y)}px`,
                  width: `${imageRef.current.width}px`,
                  height: `${imageRef.current.height}px`,
                  maxWidth: 'none'
                }}
              />
            </div>

            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: '#eff6ff',
              border: '1px solid #3b82f6',
              borderRadius: '6px',
              fontSize: '0.85rem',
              color: '#1e40af'
            }}>
              <strong>💡 How to save:</strong>
              <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: 0 }}>
                <li><strong>Windows:</strong> Press Win + Shift + S, then select the image area</li>
                <li><strong>Mac:</strong> Press Cmd + Shift + 4, then select the image area</li>
                <li><strong>Mobile:</strong> Use your device's screenshot button combination</li>
              </ul>
            </div>

            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={cancelCrop}
                style={{
                  padding: '0.75rem 2rem',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                ✓ Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      {showInfoModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowInfoModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem' }}>🔐 Encrypted File Information</h3>
              <button
                onClick={() => setShowInfoModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            <div style={{ color: '#374151', lineHeight: 1.7 }}>
              <section style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ color: '#1f2937', fontSize: '1.1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🔒 What is an Encrypted File?
                </h4>
                <p style={{ margin: 0 }}>
                  This evidence was encrypted during upload using <strong>AES-256 encryption</strong> to protect sensitive or confidential information. The file is stored as encrypted data and cannot be viewed without the correct encryption key.
                </p>
              </section>

              <section style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ color: '#1f2937', fontSize: '1.1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🔑 How to Decrypt and View
                </h4>
                <ol style={{ paddingLeft: '1.5rem', margin: 0 }}>
                  <li style={{ marginBottom: '0.5rem' }}>
                    <strong>Obtain the Encryption Key:</strong> You must have the encryption key that was used when this file was uploaded.
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    <strong>Enter the Key:</strong> Type or paste the encryption key into the "Decryption Key" field below.
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    <strong>Decrypt:</strong> Click the "Decrypt and View" button to decrypt the file.
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    <strong>View Content:</strong> Once decrypted, you can preview, download, or print the original file.
                  </li>
                </ol>
              </section>

              <section style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ color: '#1f2937', fontSize: '1.1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ⚠️ Security Notes
                </h4>
                <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
                  <li style={{ marginBottom: '0.5rem' }}>
                    Decryption happens <strong>in your browser</strong> - the key never leaves your device.
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    If you don't have the encryption key, you <strong>cannot decrypt</strong> the file. There is no recovery method.
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    Keep your encryption key secure - anyone with the key can decrypt the file.
                  </li>
                  <li>
                    The encryption key must be at least <strong>6 characters</strong> long.
                  </li>
                </ul>
              </section>

              <section style={{
                padding: '1rem',
                background: '#eff6ff',
                border: '1px solid #3b82f6',
                borderRadius: '6px'
              }}>
                <h4 style={{ color: '#1e40af', fontSize: '1rem', marginBottom: '0.5rem', margin: 0 }}>
                  💡 Tip
                </h4>
                <p style={{ margin: '0.5rem 0 0 0', color: '#1e40af', fontSize: '0.9rem' }}>
                  If you're the person who uploaded this file, check your records for the encryption key you created during upload. The system does not store encryption keys for security reasons.
                </p>
              </section>
            </div>

            <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowInfoModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EncryptedFileViewer;
